import { SCENE_CLASSES } from '../../constants/classNames';
import { EFFECT_TYPES } from '../../constants/effects';
import { joinClassNames } from '../../utils/classNameUtils';

const BEAKER_TOP = { x: 240, y: 158 };

const BURST_POINTS = Array.from({ length: 16 }, (_, index) => {
  const radius = index % 2 === 0 ? 70 : 32;
  const angle = (Math.PI * 2 * index) / 16;
  return `${BEAKER_TOP.x + radius * Math.cos(angle)},${BEAKER_TOP.y - 20 + radius * Math.sin(angle)}`;
}).join(' ');

const BUBBLES = [
  { cx: 222, r: 6, delay: '0s' },
  { cx: 238, r: 4, delay: '0.25s' },
  { cx: 252, r: 7, delay: '0.5s' },
  { cx: 262, r: 4, delay: '0.15s' },
  { cx: 230, r: 5, delay: '0.7s' },
];

function ExplosionEffect() {
  return (
    <>
      <polygon points={BURST_POINTS} fill="#ffd166" stroke="#ff7a59" strokeWidth="4" strokeLinejoin="round" />
      <circle cx={BEAKER_TOP.x} cy={BEAKER_TOP.y - 20} r="24" fill="#c77dff" />
      <text x={BEAKER_TOP.x} y={BEAKER_TOP.y - 14} textAnchor="middle" fontSize="18" fontWeight="800" fill="#ffffff">
        POP!
      </text>
    </>
  );
}

function BubbleEffect() {
  return BUBBLES.map(({ cx, r, delay }) => (
    <circle key={cx} cx={cx} cy={BEAKER_TOP.y + 20} r={r} fill="none" stroke="#ffffff" strokeWidth="2.5" style={{ animationDelay: delay }} />
  ));
}

function FlashEffect() {
  return <circle cx={BEAKER_TOP.x} cy={BEAKER_TOP.y} r="90" fill="url(#flash-gradient)" />;
}

function GlowEffect() {
  return <circle cx={BEAKER_TOP.x} cy={BEAKER_TOP.y + 10} r="60" fill="url(#glow-gradient)" />;
}

function SlowChangeEffect() {
  return (
    <text x={BEAKER_TOP.x + 48} y={BEAKER_TOP.y - 8} fontSize="22" fontWeight="700" fill="#b5582a">
      ⏳
    </text>
  );
}

function NoReactionEffect() {
  return (
    <text x={BEAKER_TOP.x} y={BEAKER_TOP.y - 12} textAnchor="middle" fontSize="30" fontWeight="800" fill="#8a9bb3">
      ?
    </text>
  );
}

/**
 * Two blobs of molten metal swirling together into one shiny alloy.
 */
function MeltEffect() {
  return (
    <>
      <ellipse cx={BEAKER_TOP.x - 14} cy={BEAKER_TOP.y - 14} rx="16" ry="11" fill="url(#melt-gradient)" />
      <ellipse cx={BEAKER_TOP.x + 14} cy={BEAKER_TOP.y - 14} rx="16" ry="11" fill="url(#melt-gradient)" />
      <path
        d={`M${BEAKER_TOP.x - 20} ${BEAKER_TOP.y - 16} q10 -8 20 0 t20 0`}
        stroke="#ffffff"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        opacity="0.8"
      />
    </>
  );
}

const EFFECT_COMPONENTS = {
  [EFFECT_TYPES.EXPLOSION]: ExplosionEffect,
  [EFFECT_TYPES.FIZZ]: BubbleEffect,
  [EFFECT_TYPES.FLASH]: FlashEffect,
  [EFFECT_TYPES.GLOW]: GlowEffect,
  [EFFECT_TYPES.SLOW_CHANGE]: SlowChangeEffect,
  [EFFECT_TYPES.MELT]: MeltEffect,
  [EFFECT_TYPES.NONE]: NoReactionEffect,
};

/**
 * Drawn above the beaker while a reaction plays. Animations live in LabScene.css.
 */
export default function ReactionEffect({ effect }) {
  const EffectComponent = EFFECT_COMPONENTS[effect];
  if (!EffectComponent) return null;
  return (
    <g className={joinClassNames(SCENE_CLASSES.effect, `${SCENE_CLASSES.effectPrefix}${effect}`)}>
      <EffectComponent />
    </g>
  );
}

/**
 * Gradients the effects refer to. Rendered once inside the scene's <defs>.
 */
export function ReactionEffectGradients() {
  return (
    <>
      <radialGradient id="flash-gradient">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="45%" stopColor="#fff9c4" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#fff9c4" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="melt-gradient" cx="35%" cy="35%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="45%" stopColor="#d9dee6" />
        <stop offset="100%" stopColor="#8f9bab" />
      </radialGradient>
      <radialGradient id="glow-gradient">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="35%" stopColor="#ffe08a" stopOpacity="0.9" />
        <stop offset="70%" stopColor="#ff9f43" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#ff9f43" stopOpacity="0" />
      </radialGradient>
    </>
  );
}
